import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHero } from "@/components/common/PageHero";
import { Icon } from "@/components/common/Icon";
import { CTASection } from "@/components/home/CTASection";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { useT } from "@/context/LanguageContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useT();
  const [service, setService] = useState(null);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setService(false));
    api.get("/services").then((r) => setOthers(r.data)).catch((e) => console.error("Failed to load services list:", e));
  }, [slug, lang]);

  const otherServices = useMemo(() => others.filter((o) => o.slug !== slug).slice(0, 6), [others, slug]);

  if (service === false) return <div className="pt-40 pb-24 text-center text-slate-500">Service not found.</div>;
  if (!service) return <div className="pt-40 pb-24 text-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="service-detail-page">
      <PageHero
        overline="Service"
        title={service.title}
        subtitle={service.tagline}
        crumbs={[{ label: "Services", to: "/services" }, { label: service.title }]}
        image={service.image}
      />
      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white">
              <Icon name={service.icon} size={28} weight="duotone" />
            </div>
            <p className="mt-6 text-lg leading-relaxed text-slate-700">{service.summary}</p>
            <h2 className="mt-10 font-heading text-2xl font-semibold text-ink">What's included</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {(service.features || []).map((f) => (
                <div key={f} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-mist p-5">
                  <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-teal" />
                  <p className="text-sm font-medium text-ink">{f}</p>
                </div>
              ))}
            </div>
            <img src={service.image} alt={service.title} className="mt-10 h-80 w-full rounded-xl object-cover" />
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-xl border border-slate-200 bg-ink p-7 text-white">
                <h3 className="font-heading text-lg font-semibold">Need this service?</h3>
                <p className="mt-2 text-sm text-slate-300">Get a tailored quote within 24–48 hours.</p>
                <Link to="/contact?type=quote" data-testid="service-detail-quote" className="mt-5 flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-5 py-3 text-sm font-semibold">
                  Request a Quote <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
              <div className="rounded-xl border border-slate-200 p-6">
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">Other services</h3>
                <ul className="mt-4 space-y-3">
                  {otherServices.map((o) => (
                    <li key={o.slug}>
                      <Link to={`/services/${o.slug}`} className="flex items-center justify-between text-sm text-slate-700 hover:text-teal">
                        {o.title} <ArrowRight size={14} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
